import { Component, ContentChild, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NpMdiTabOutletDirective } from './np-mdi-tab-outlet.directive';
import { NpMdiHomeComponent } from './np-mdi-home.component';
import { NpMdiConfig, NpMdiData, NpMdiOpenOptions, NpMdiService, NpMdiTabRef } from './np-mdi.service';

/**
 * MDI 탭
 * <p>
 * 페이지가 open()으로 연 화면이 탭으로 추가된다.
 * 안에 np-mdi-home을 두면 그 내용이 닫을 수 없는 첫 번째 탭(Home)으로 고정되고, 없으면 Home이 없는 MDI가 된다.
 * <ul>
 * <li>탭 목록은 NpMdiService가 관리하며 np-mdi마다 하나씩 갖는다. 한 페이지에 np-mdi가 여러 개여도 섞이지 않는다</li>
 * <li>페이지는 템플릿의 np-mdi를 가져와서(#mdi1 + ViewChild) open()을 호출한다. 열린 탭(mdiData)은 np-mdi가 관리하고,
 * 페이지는 getMdiData(), isOpen()으로 참조만 한다</li>
 * <li>탭이 닫히면 알린다. (closed): 화면이 닫기를 요청(embedClose) / (dismissed): 화면의 취소(embedDismiss), 탭의 X,
 * 모두 닫기</li>
 * <li>설정은 [config]로 넘긴다. 없으면 기본 설정. 예: 최대 탭 수 max (없으면 20, 0이면 무제한. 가득 차면 새 창을 열지 않고 알림을 띄운다. 자동으로 닫지 않는다)</li>
 * <li>np-mdi 안의 화면(탭으로 열린 화면, Home 안의 컴포넌트)은 NpMdiService를 주입받아 다른 화면을 연다</li>
 * <li>키는 여는 쪽이 만든다. 같은 키가 열려 있으면 그 탭으로 이동</li>
 * <li>단축키 Ctrl + X: 활성 탭을 닫는다(dismissed). Home에서는 동작하지 않고, 입력란에 포커스가 있으면 잘라내기가 그대로 동작한다</li>
 * <li>탭 우클릭 메뉴: 닫기 / 다른 탭 닫기 / 오른쪽 탭 닫기 / 모두 닫기</li>
 * <li>저장하지 않은 화면: 화면 컴포넌트의 dirty 또는 editing이 true면 탭에 점으로 표시하고,
 * MDI가 닫을 때(탭의 X, 가운데 버튼, Ctrl + X, 메뉴) 확인을 받는다. 화면이 스스로 닫는 경우(embedClose, embedDismiss)는 화면이 처리한다</li>
 * <li>Home 탭은 탭 바 스크롤 영역 밖에 고정되어 탭이 많아도 항상 보인다</li>
 * <li>비활성 탭은 [hidden] + 변경 감지 분리(NpMdiTabOutletDirective). Home은 [hidden]만 한다</li>
 * <li>레이아웃 콘텐츠 영역의 좌우/아래 여백을 없애 화면을 가득 채운다</li>
 * </ul>
 *
 * <pre>
 * // 페이지 컴포넌트: @ViewChild('mdi1') mdi1!: NpMdiComponent;  →  this.mdi1.open({ key, title, component, inputs })
 * &lt;np-mdi #mdi1 [config]="{ max: 10 }" (closed)="search()" (dismissed)="..."&gt;
 *     &lt;np-mdi-home title="WORK ORDER 관리" icon="fa-house"&gt;
 *         ... 페이지 내용 (Home) ...
 *     &lt;/np-mdi-home&gt;
 * &lt;/np-mdi&gt;
 * </pre>
 */
@Component({
  selector: 'np-mdi',
  standalone: true,
  imports: [NpMdiTabOutletDirective],
  // np-mdi마다 탭 관리 서비스를 하나씩 갖는다. (한 페이지에 np-mdi가 여러 개여도 탭 목록이 섞이지 않는다.)
  providers: [NpMdiService],
  templateUrl: './np-mdi.component.html',
  styleUrl: './np-mdi.component.scss'
})
export class NpMdiComponent implements OnChanges {

  ///////////////////////////////////////////////////////////////////////////////////////////////////// HOME

  /** np-mdi 안에 정의한 Home. 없으면 Home이 없는 MDI */
  @ContentChild(NpMdiHomeComponent) home?: NpMdiHomeComponent;

  ///////////////////////////////////////////////////////////////////////////////////////////////////// INJECT

  /** 이 np-mdi의 탭 관리 서비스 */
  mdi = inject(NpMdiService);

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.mdi.changed$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.afterTabsChanged());
    this.mdi.closed$.pipe(takeUntilDestroyed(destroyRef)).subscribe(data => this.closed.emit(data));
    this.mdi.dismissed$.pipe(takeUntilDestroyed(destroyRef)).subscribe(data => this.dismissed.emit(data));
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// CONFIG

  /** 설정. 없으면 기본 설정(NP_MDI_DEFAULT_CONFIG), 있으면 그 설정으로 초기화한다. 예: [config]="{ max: 10 }" */
  @Input() config?: NpMdiConfig;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      this.mdi.configure(this.config);
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// MDI DATA
  // 페이지는 템플릿의 np-mdi를 가져와서(#mdi1 + ViewChild) 사용한다. 열린 탭은 np-mdi가 관리한다.

  /** 화면이 닫기를 요청해서(embedClose) 탭이 닫힐 때 (닫힌 탭의 키, 타이틀, 입력값) */
  @Output() closed = new EventEmitter<NpMdiData>();

  /** 그 외의 경우로 탭이 닫힐 때: 화면의 취소(embedDismiss), 탭의 X, 모두 닫기 (닫힌 탭의 키, 타이틀, 입력값) */
  @Output() dismissed = new EventEmitter<NpMdiData>();

  /** np-mdi가 관리하는 현재 열린 탭 목록을 참조용 정보(키, 타이틀, 입력값)로 돌려준다. 복사본이라 고쳐도 MDI에는 영향이 없다. */
  getMdiData(): NpMdiData[] {
    return this.mdi.getMdiData();
  }

  /** 화면을 탭으로 연다. 같은 키가 열려 있으면 그 탭으로 이동한다. 최대 탭 수까지 차 있으면 열지 않고 알림을 띄운다. (undefined) */
  open(options: NpMdiOpenOptions): NpMdiTabRef | undefined {
    return this.mdi.open(options);
  }

  isOpen(key: string): boolean {
    return this.mdi.isOpen(key);
  }

  closeByKey(key: string) {
    this.mdi.closeByKey(key);
  }

  dismissByKey(key: string) {
    this.mdi.dismissByKey(key);
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// VIEW

  @ViewChild('tabBar') tabBarRef?: ElementRef<HTMLElement>;

  tabOverflow: boolean = false;
  tabListOpen: boolean = false;

  activateHome() {
    this.tabListOpen = false;
    this.mdi.activateHome();
  }

  activate(id: number) {
    this.tabListOpen = false;
    this.mdi.activate(id);
  }

  /** 탭의 X로 닫기 (dismissed) */
  dismiss(id: number, event?: Event) {
    event?.stopPropagation();
    if (!this.confirmDismiss([id])) return;
    this.mdi.dismiss(id);
  }

  dismissOthers() {
    this.tabListOpen = false;
    const exceptId = this.mdi.activeId ?? undefined;
    if (!this.confirmDismiss(this.mdi.tabs.filter(t => t.id !== exceptId).map(t => t.id))) return;
    this.mdi.dismissAll(exceptId);
  }

  dismissAll() {
    this.tabListOpen = false;
    if (!this.confirmDismiss(this.mdi.tabs.map(t => t.id))) return;
    this.mdi.dismissAll();
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// DIRTY

  @ViewChildren(NpMdiTabOutletDirective) private outlets?: QueryList<NpMdiTabOutletDirective>;

  /** 탭 화면에 저장하지 않은 수정 내용이 있는지 (화면 컴포넌트의 dirty 또는 editing) */
  isDirty(id: number): boolean {
    const instance = this.outlets?.find(o => o.npMdiTabOutletTabId === id)?.instance;
    return instance?.dirty === true || instance?.editing === true;
  }

  /** 닫을 탭 중 저장하지 않은 탭이 있으면 확인을 받는다. 닫아도 되면 true */
  private confirmDismiss(ids: number[]): boolean {
    const dirtyCount = ids.filter(id => this.isDirty(id)).length;
    if (dirtyCount === 0) return true;

    const message = ids.length === 1
      ? '저장하지 않은 수정 내용이 있습니다. 닫으시겠습니까?'
      : `저장하지 않은 수정 내용이 있는 탭이 ${dirtyCount}개 있습니다. 닫으시겠습니까?`;
    return confirm(message);
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// TAB MENU (우클릭)

  /** 우클릭 메뉴. tabId가 null이면 Home에서 연 메뉴 */
  tabMenu: { tabId: number | null; x: number; y: number } | null = null;

  openTabMenu(event: MouseEvent, tabId: number | null) {
    event.preventDefault();
    event.stopPropagation();
    this.tabListOpen = false;
    this.tabMenu = { tabId, x: event.clientX, y: event.clientY };
  }

  closeTabMenu() {
    this.tabMenu = null;
  }

  /** 메뉴 기준 탭의 오른쪽에 탭이 있는지 (Home은 모든 탭이 오른쪽) */
  hasRightTabs(tabId: number | null): boolean {
    if (tabId === null) return this.mdi.tabs.length > 0;
    return this.mdi.tabs.findIndex(t => t.id === tabId) < this.mdi.tabs.length - 1;
  }

  menuDismiss(tabId: number) {
    this.closeTabMenu();
    this.dismiss(tabId);
  }

  /** 기준 탭만 남기고 닫는다. 기준 탭으로 이동 (Home이면 모두 닫기) */
  menuDismissOthers(tabId: number | null) {
    this.closeTabMenu();
    const ids = this.mdi.tabs.filter(t => t.id !== tabId).map(t => t.id);
    if (!this.confirmDismiss(ids)) return;
    if (tabId === null) {
      this.mdi.dismissAll();
    } else {
      this.mdi.activate(tabId);
      this.mdi.dismissAll(tabId);
    }
  }

  menuDismissRight(tabId: number | null) {
    this.closeTabMenu();
    const idx = tabId === null ? -1 : this.mdi.tabs.findIndex(t => t.id === tabId);
    const ids = this.mdi.tabs.slice(idx + 1).map(t => t.id);
    if (!this.confirmDismiss(ids)) return;
    if (tabId === null) {
      this.mdi.dismissAll();
    } else {
      this.mdi.dismissRightOf(tabId);
    }
  }

  menuDismissAll() {
    this.closeTabMenu();
    this.dismissAll();
  }

  /** 메뉴 밖을 누르거나, 다른 곳에서 우클릭, 창 크기 변경, 창 전환, Esc(onDocumentKeydown) 때 메뉴를 닫는다. */
  @HostListener('document:click')
  @HostListener('document:contextmenu')
  @HostListener('window:resize')
  @HostListener('window:blur')
  onDocumentClickForMenu() {
    if (this.tabMenu) this.closeTabMenu();
  }

  /** 가운데 버튼 클릭으로 닫기 */
  onTabAuxClick(event: MouseEvent, id: number) {
    if (event.button === 1) this.dismiss(id, event);
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// SHORTCUT

  private elRef: ElementRef<HTMLElement> = inject(ElementRef);

  /**
   * Ctrl + X: 활성 탭을 닫는다. (dismissed) Home이 보이는 상태면 아무것도 하지 않는다.
   * <p>
   * Ctrl + X는 브라우저의 잘라내기이므로, 잘라내기가 필요한 곳(입력란)에 포커스가 있으면 닫지 않고 잘라내기를 그대로 둔다.
   * 모달이 떠 있거나 이 np-mdi가 화면에 보이지 않을 때(다른 탭 안에 숨겨진 np-mdi)도 닫지 않는다.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    // 우클릭 메뉴가 열려 있으면 Esc로 닫는다.
    if (event.key === 'Escape' && this.tabMenu) {
      this.closeTabMenu();
      return;
    }

    // 한글 입력 상태에서도 같은 키로 잡히도록 key가 아닌 code로 비교한다.
    if (event.code !== 'KeyX' || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
    if (event.repeat || event.defaultPrevented) return;
    if (this.mdi.activeId === null) return;
    if (this.isEditable(event.target)) return;
    if (document.body.classList.contains('modal-open')) return;
    if (this.elRef.nativeElement.offsetParent === null) return;

    event.preventDefault();
    this.dismiss(this.mdi.activeId);
  }

  private isEditable(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return target.isContentEditable || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// TAB BAR

  /** 렌더링 후 활성 탭이 보이도록 스크롤하고 넘침 여부를 갱신 */
  private afterTabsChanged() {
    setTimeout(() => {
      const bar = this.tabBarRef?.nativeElement;
      if (!bar) return;
      bar.querySelector<HTMLElement>('.np-mdi-tab.is-active')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      this.updateOverflow();
    });
  }

  updateOverflow() {
    const bar = this.tabBarRef?.nativeElement;
    this.tabOverflow = !!bar && bar.scrollWidth > bar.clientWidth + 1;
  }

  scrollTabs(dir: -1 | 1) {
    this.tabBarRef?.nativeElement.scrollBy({ left: dir * 240, behavior: 'smooth' });
  }

  /** 마우스 휠(세로)을 탭 바 가로 스크롤로 변환 */
  onTabBarWheel(event: WheelEvent) {
    const bar = this.tabBarRef?.nativeElement;
    if (!bar || !this.tabOverflow || event.deltaX !== 0) return;
    bar.scrollLeft += event.deltaY;
    event.preventDefault();
  }

}
